import React from 'react';
import { noteIndex, getNoteName, usesFlats } from '../../lib/theory';
import { BASS_FORMULAS, CHORD_DEGREE_COLORS } from '../../lib/upright-bass-theory';
import { Tray } from '../shared';

// Diatonic qualities in the major scale (degrees 1-7)
const DIATONIC_META = [
  { roman: 'I',    quality: 'major' },
  { roman: 'ii',   quality: 'minor' },
  { roman: 'iii',  quality: 'minor' },
  { roman: 'IV',   quality: 'major' },
  { roman: 'V',    quality: 'major' },
  { roman: 'vi',   quality: 'minor' },
  { roman: 'vii°', quality: 'dim'   },
];

/**
 * Derive the 7 diatonic chord names for a given major key.
 * Returns [ { degree, roman, chordName, quality, color } ]
 */
export function getDiatonicChords(musicalKey) {
  const rootIdx  = noteIndex(musicalKey);
  const formula  = BASS_FORMULAS['Major'];
  const flats    = usesFlats(musicalKey, formula, 'major');

  return DIATONIC_META.map((meta, i) => {
    const scaleDegree = i + 1;
    const chordRoot   = getNoteName((rootIdx + formula[i]) % 12, flats);
    const suffix =
      meta.quality === 'minor' ? 'm' :
      meta.quality === 'dim'   ? '°' :
      '';
    return {
      degree:    scaleDegree,
      roman:     meta.roman,
      chordName: chordRoot + suffix,
      quality:   meta.quality,
      color:     CHORD_DEGREE_COLORS[scaleDegree],
    };
  });
}

/**
 * UprightBassChordChanges
 *
 * Multi-select Tray of the key's 7 chords for the Chord changes card. Each option shows the
 * chord name over its Roman numeral, with a short bar in the degree's colour underneath. The
 * bar uses the same `--chord-*` token as the fingerboard dots, so it ties each option to its
 * dots. Degrees are always computed off the major scale.
 *
 * Props:
 *   musicalKey:       string    - current root key
 *   selectedDegrees:  number[]  - 1-indexed selected degrees, e.g. [1, 4, 5]
 *   onDegreeToggle:   (degree: number) => void
 */
export default function UprightBassChordChanges({ musicalKey, selectedDegrees, onDegreeToggle }) {
  const chords = getDiatonicChords(musicalKey);
  const options = chords.map(({ degree, roman, chordName, color }) => {
    const on = selectedDegrees.includes(degree);
    return {
      value: String(degree),
      ariaLabel: `${roman} chord, ${chordName}`,
      label: (
        <span className="flex flex-col items-center gap-[2px] leading-none">
          <span className="text-[14px]">{chordName}</span>
          <span className="text-[10px] font-semibold">{roman}</span>
          <span
            aria-hidden="true"
            className="h-[3px] w-4 rounded-full"
            style={{ background: `hsl(var(${color.fillVar}))`, opacity: on ? 1 : 0.4 }}
          />
        </span>
      ),
    };
  });

  return (
    <Tray
      multiple
      ariaLabel="Chords"
      options={options}
      value={selectedDegrees.map(String)}
      onChange={(v) => onDegreeToggle(Number(v))}
      columns={options.length}
      optionClassName="h-[48px] lg:h-[52px]"
    />
  );
}
